import type { components } from '@fluencelabs/vodopad-admin-api'
import { useQuery } from '@tanstack/react-query'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'

export type BasicRole = components['schemas']['BasicUserRoleDto']
type RolesResponse = components['schemas']['PaginatedResponse_BasicUserRoleDto']
type FetchRolesPage = (page: number) => Promise<RolesResponse>

const PAGE_SIZE = 100
const MAX_PAGES = 100

export const ROLES_QUERY_KEY = ['roles', 'all'] as const

async function fetchRolesPage(page: number): Promise<RolesResponse> {
  const result = await client.GET('/v1/admin/user_roles', {
    params: { query: { page, perPage: PAGE_SIZE } },
  })
  return unwrapApiData(result, 'Failed to fetch roles')
}

export async function fetchAllRoles(
  fetchPage: FetchRolesPage = fetchRolesPage,
): Promise<BasicRole[]> {
  const roles: BasicRole[] = []

  for (let page = 1; page <= MAX_PAGES; page++) {
    const response = await fetchPage(page)
    roles.push(...response.items)

    const totalPages = response.pagination?.totalPages
    const hasValidTotal = Number.isSafeInteger(totalPages) && totalPages >= page

    if (
      response.items.length === 0 ||
      (hasValidTotal ? page >= totalPages : response.items.length < PAGE_SIZE)
    ) {
      return roles
    }
  }

  throw new Error('Role pagination exceeded 100 pages (10,000 roles)')
}

/** Lists backend roles for role-management surfaces. */
export function useRoles(enabled = true) {
  const query = useQuery({
    queryKey: ROLES_QUERY_KEY,
    queryFn: () => fetchAllRoles(),
    enabled,
    staleTime: 5 * 60 * 1000,
  })

  return {
    roles: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
